// apps/web/src/pages/estoque/contagem-grade.tsx — Fase 13, Plano 27 (EST-13, D-24, D-26).
// Grade da contagem CEGA: Item · Un. · Quantidade contada (+ Custo inicial na abertura de matéria-prima própria). Recebe
// apenas `ItemContagemCega` (sem nenhum dado de saldo do sistema): nada aqui pode mostrar o que o sistema acha que existe.
// Campo vazio é "Não contado" (diferente de 0); quem não tem nada na prateleira digita 0. Com centenas de itens a grade é
// virtualizada (@tanstack/react-virtual); "Só não contados" filtra, Enter vai à linha seguinte e os campos têm 44px no tablet.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ItemContagemCega } from '@pieltec/contracts';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { formatarMoeda } from '../../lib/vendas/formatacao';
import {
  LIMITE_VIRTUALIZACAO_CONTAGEM,
  MENSAGEM_CUSTO_OBRIGATORIO,
  MENSAGEM_NAO_CONTADO,
  edicaoDaLinha,
  naoContados,
  proximaLinha,
  type EdicaoContagem,
  type EdicaoLinha,
  type ErroLinha,
} from '../../lib/estoque/inventario';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ALTURA_LINHA_ESTIMADA = 72;

export interface ContagemGradeProps {
  readonly itens: readonly ItemContagemCega[];
  readonly edicao: EdicaoContagem;
  readonly aoAlterar: (itemId: string, parcial: Partial<EdicaoLinha>) => void;
  /** Abertura de matéria-prima própria: mostra "Custo inicial (R$/un)". */
  readonly comCusto: boolean;
  readonly erros: Readonly<Record<string, ErroLinha>>;
  readonly desabilitada: boolean;
}

function placeholderCusto(item: ItemContagemCega): string {
  return item.custoSugeridoUltimaNf === null ? MENSAGEM_CUSTO_OBRIGATORIO : `Última NF: ${formatarMoeda(item.custoSugeridoUltimaNf, 4)}`;
}

interface LinhaContagemProps {
  readonly item: ItemContagemCega;
  readonly indice: number;
  readonly linha: EdicaoLinha;
  readonly comCusto: boolean;
  readonly erro: ErroLinha | undefined;
  readonly desabilitada: boolean;
  readonly aoAlterar: ContagemGradeProps['aoAlterar'];
  readonly aoEnter: (indice: number) => void;
}

function LinhaContagem({ item, indice, linha, comCusto, erro, desabilitada, aoAlterar, aoEnter }: LinhaContagemProps) {
  function teclar(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    aoEnter(indice);
  }

  return (
    <tr className="border-t align-top" data-index={indice}>
      <td className="w-full max-w-0 px-3 py-2">
        <div className="font-semibold tabular-nums">{item.codigo}</div>
        <div className="truncate text-muted-foreground" title={item.descricao}>{item.descricao}</div>
      </td>
      <td className="hidden px-3 py-3 sm:table-cell">{item.unidade}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-2">
          <Input
            id={`contagem-q-${item.itemId}`}
            inputMode="decimal"
            autoComplete="off"
            enterKeyHint="next"
            disabled={desabilitada}
            value={linha.quantidade}
            placeholder={MENSAGEM_NAO_CONTADO}
            aria-label={`Quantidade contada de ${item.codigo}`}
            aria-invalid={erro?.quantidade !== undefined}
            onChange={(e) => aoAlterar(item.itemId, { quantidade: e.target.value })}
            onKeyDown={teclar}
            className="min-h-11 w-32 text-right tabular-nums sm:min-h-9"
          />
          <span className="text-xs text-muted-foreground sm:hidden" aria-hidden="true">
            {item.unidade}
          </span>
        </div>
        {erro?.quantidade && <p className="mt-1 max-w-48 text-xs text-rose-700">{erro.quantidade}</p>}
      </td>
      {comCusto && (
        <td className="px-3 py-2">
          <Input
            id={`contagem-c-${item.itemId}`}
            inputMode="decimal"
            autoComplete="off"
            enterKeyHint="next"
            disabled={desabilitada}
            value={linha.custo}
            placeholder={placeholderCusto(item)}
            aria-label={`Custo inicial de ${item.codigo} (R$/${item.unidade})`}
            aria-invalid={erro?.custo !== undefined}
            onChange={(e) => aoAlterar(item.itemId, { custo: e.target.value })}
            onKeyDown={teclar}
            className="min-h-11 w-44 text-right tabular-nums sm:min-h-9"
          />
          {erro?.custo && <p className="mt-1 max-w-48 text-xs text-rose-700">{erro.custo}</p>}
        </td>
      )}
    </tr>
  );
}

function Cabecalho({ comCusto }: { comCusto: boolean }) {
  return (
    <thead className="sticky top-0 z-10 bg-slate-100 text-left">
      <tr>
        <th scope="col" className={CABECALHO}>Item</th>
        <th scope="col" className={`${CABECALHO} hidden sm:table-cell`}>Un.</th>
        <th scope="col" className={`${CABECALHO} text-right`}>Quantidade contada</th>
        {comCusto && <th scope="col" className={`${CABECALHO} text-right`}>Custo inicial (R$/un)</th>}
      </tr>
    </thead>
  );
}

export function ContagemGrade({ itens, edicao, aoAlterar, comCusto, erros, desabilitada }: ContagemGradeProps) {
  const rolagemRef = useRef<HTMLDivElement>(null);
  const edicaoRef = useRef(edicao);
  edicaoRef.current = edicao;
  const [soNaoContados, setSoNaoContados] = useState(false);
  const [idsFiltrados, setIdsFiltrados] = useState<ReadonlySet<string> | null>(null);

  // O filtro "Só não contados" congela a lista no momento em que é ligado (e quando os itens são recarregados/salvos):
  // a linha que acabou de ser digitada não some debaixo do dedo do contador.
  useEffect(() => {
    setIdsFiltrados(soNaoContados ? new Set(naoContados(itens, edicaoRef.current).map((item) => item.itemId)) : null);
  }, [soNaoContados, itens]);

  const exibidos = useMemo(() => (idsFiltrados ? itens.filter((item) => idsFiltrados.has(item.itemId)) : itens), [itens, idsFiltrados]);
  const virtualizar = exibidos.length > LIMITE_VIRTUALIZACAO_CONTAGEM;

  // Hook incondicional: com count 0 não faz nada quando a grade é pequena.
  const virtualizador = useVirtualizer({
    count: virtualizar ? exibidos.length : 0,
    getScrollElement: () => rolagemRef.current,
    estimateSize: () => ALTURA_LINHA_ESTIMADA,
    overscan: 8,
  });

  function focarLinha(indice: number): void {
    const alvo = exibidos[indice];
    if (!alvo) return;
    if (virtualizar) virtualizador.scrollToIndex(indice);
    // A linha seguinte pode ainda não existir no DOM (virtualização): tenta nos próximos quadros.
    let tentativas = 0;
    const tentar = () => {
      const campo = document.getElementById(`contagem-q-${alvo.itemId}`);
      if (campo) {
        campo.focus();
        return;
      }
      tentativas += 1;
      if (tentativas < 10) window.requestAnimationFrame(tentar);
    };
    window.requestAnimationFrame(tentar);
  }

  function aoEnter(indice: number): void {
    const proxima = proximaLinha(indice, exibidos.length);
    if (proxima !== null) focarLinha(proxima);
  }

  const renderLinha = (item: ItemContagemCega, indice: number) => (
    <LinhaContagem
      key={item.itemId}
      item={item}
      indice={indice}
      linha={edicao[item.itemId] ?? edicaoDaLinha(item)}
      comCusto={comCusto}
      erro={erros[item.itemId]}
      desabilitada={desabilitada}
      aoAlterar={aoAlterar}
      aoEnter={aoEnter}
    />
  );

  const virtuais = virtualizar ? virtualizador.getVirtualItems() : [];
  const antes = virtuais.length > 0 ? (virtuais[0]?.start ?? 0) : 0;
  const depois = virtuais.length > 0 ? virtualizador.getTotalSize() - (virtuais[virtuais.length - 1]?.end ?? 0) : 0;

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <p className="text-xs text-muted-foreground">Se não há nada, digite 0.</p>
        <label htmlFor="contagem-so-nao-contados" className="flex min-h-11 cursor-pointer items-center gap-2 text-sm sm:min-h-9">
          <Checkbox
            id="contagem-so-nao-contados"
            checked={soNaoContados}
            onCheckedChange={(marcada) => setSoNaoContados(marcada === true)}
            className="size-5 sm:size-4"
          />
          Só não contados
        </label>
      </div>

      {exibidos.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-muted-foreground">Todos os itens já foram contados.</p>
      ) : virtualizar ? (
        <div ref={rolagemRef} className="max-h-[65vh] overflow-y-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Contagem de itens do inventário</caption>
            <Cabecalho comCusto={comCusto} />
            <tbody aria-hidden="true">
              <tr style={{ height: antes }} />
            </tbody>
            {virtuais.map((virtual) => {
              const item = exibidos[virtual.index];
              if (!item) return null;
              return (
                <tbody key={item.itemId} data-index={virtual.index} ref={virtualizador.measureElement}>
                  {renderLinha(item, virtual.index)}
                </tbody>
              );
            })}
            <tbody aria-hidden="true">
              <tr style={{ height: depois }} />
            </tbody>
          </table>
        </div>
      ) : (
        <table className="w-full text-sm">
          <caption className="sr-only">Contagem de itens do inventário</caption>
          <Cabecalho comCusto={comCusto} />
          <tbody>{exibidos.map(renderLinha)}</tbody>
        </table>
      )}
    </div>
  );
}
